"use client";

import { c as _c } from "react/compiler-runtime";
import { ColorModeContext } from "@contexts/color-mode";
import { ChatRequestError, listConversations, listMessages, postChat, type ChatSource } from "@/lib/chatbot/api";
import { prependOlderMessages } from "@/lib/chatbot/messages";
import { useChatbotStore } from "@store/useChatbotStore";
import { Button } from "@components/ui/styled-button";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/Popover";
import { cn } from "@utils/cn";
import { BookOpen, Bot, History, RotateCcw, SendHorizontal, X } from "lucide-react";
import { marked } from "marked";
import { useParams } from "next/navigation";
import { useCallback, useContext, useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
const STARTER_PROMPTS = ["What is the refund policy?", "How do memberships work?", "What are the guest invitation rules?"];
function sanitizeHtml(html: string): string {
  return html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "").replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "").replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, "").replace(/\son\w+=(?:"[^"]*"|'[^']*')/gi, "").replace(/javascript:/gi, "");
}
function sourceLabel(source: ChatSource): string {
  const trail = [source.h1, source.h2, source.h3].filter(Boolean).join(" / ");
  return trail || source.object_name || "Source";
}
function stripCitedTrail(answer: string, sources: ChatSource[]): string {
  const labels = new Set(sources.map(sourceLabel).filter(label => label && label !== "Source"));
  if (labels.size === 0) return answer;
  const lines = answer.replace(/\s+$/, "").split("\n");
  while (lines.length > 0) {
    const last = lines[lines.length - 1].trim();
    if (!last || labels.has(last)) {
      lines.pop();
      continue;
    }
    break;
  }
  return lines.join("\n").trimEnd();
}
function formatRuleMarkdown(text: string): string {
  return text.replace(/(?:^|\n)([A-Z][A-Za-z0-9][A-Za-z0-9 ]{0,40}):\s+([^\n]+)/g, "\n- **$1:** $2");
}
function renderAnswer(markdown: string, sources: ChatSource[] = []): string {
  const prepared = formatRuleMarkdown(stripCitedTrail(markdown, sources));
  const html = marked.parse(prepared, {
    async: false
  }) as string;
  return sanitizeHtml(html);
}
function TypingIndicator(t0) {
  const $ = _c(7);
  const {
    color
  } = t0;
  const [dotCount, setDotCount] = useState(1);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const id = window.setInterval(() => {
        setDotCount(_temp);
      }, 450);
      return () => window.clearInterval(id);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== color || $[3] !== dotCount) {
    let t4;
    if ($[5] !== color) {
      t4 = (_, index) => <span key={index} className="size-2 rounded-full" style={{
        backgroundColor: color
      }} />;
      $[5] = color;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t3 = <div className="mr-auto flex items-center rounded-2xl rounded-bl-md border border-border bg-card px-3 py-2.5 shadow-sm" role="status" aria-live="polite" aria-label="Assistant is thinking"><span className="flex h-2 w-8 items-center gap-1">{Array.from({
          length: dotCount
        }, t4)}</span></div>;
    $[2] = color;
    $[3] = dotCount;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function _temp(count) {
  return count % 3 + 1;
}
function SourceButton(t0) {
  const $ = _c(6);
  const {
    sources
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PopoverTrigger asChild={true}><button type="button" aria-label="View sources" title="Sources" className="mt-1.5 inline-flex size-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background hover:text-foreground"><BookOpen className="size-3.5" /></button></PopoverTrigger>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="mb-1.5 shrink-0 px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Sources</p>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== sources) {
    t3 = sources.map(_temp4);
    $[2] = sources;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <Popover>{t1}<PopoverContent align="start" side="top" className="flex max-h-[min(240px,50vh)] w-64 flex-col overflow-hidden p-2" onOpenAutoFocus={_temp2} onWheel={_temp3}>{t2}<ul className="styled-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain">{t3}</ul></PopoverContent></Popover>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function _temp4(source, index) {
  return <li key={`${source.object_name ?? "src"}-${index}`} className="rounded-md px-1.5 py-1 text-[11px] leading-snug text-foreground">{sourceLabel(source)}</li>;
}
function _temp3(event_0) {
  return event_0.stopPropagation();
}
function _temp2(event) {
  return event.preventDefault();
}
function SessionPicker(t0) {
  const $ = _c(22);
  const {
    disabled,
    onSelect,
    onLoadMore
  } = t0;
  const conversations = useChatbotStore(_temp5);
  const conversationId = useChatbotStore(_temp6);
  const listCursor = useChatbotStore(_temp7);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <History className="size-4" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = disabled || conversations.length === 0;
  let t3;
  if ($[1] !== t2) {
    t3 = <PopoverTrigger asChild={true}><Button type="button" variant="text" iconOnly={true} className="h-8 text-white hover:bg-white/10" icon={t1} buttonLabel="Past chats" disabled={t2} /></PopoverTrigger>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="mb-1.5 shrink-0 px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Past chats</p>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== conversationId || $[5] !== conversations || $[6] !== onSelect) {
    let t6;
    if ($[8] !== conversationId || $[9] !== onSelect) {
      t6 = conversation => <li key={conversation.id}><button type="button" onClick={() => onSelect(conversation.id)} className={cn("w-full rounded-md px-1.5 py-1.5 text-left text-[11px] leading-snug hover:bg-muted", conversation.id === conversationId && "bg-muted font-medium")}><span className="line-clamp-2">{conversation.title?.trim() || "New chat"}</span></button></li>;
      $[8] = conversationId;
      $[9] = onSelect;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    t5 = conversations.map(t6);
    $[4] = conversationId;
    $[5] = conversations;
    $[6] = onSelect;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <ul className="styled-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain">{t5}</ul>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== listCursor || $[14] !== onLoadMore) {
    t7 = listCursor && <button type="button" className="mt-1 w-full rounded-md px-1.5 py-1.5 text-left text-[11px] text-muted-foreground hover:bg-muted" onClick={onLoadMore}>Older chats</button>;
    $[13] = listCursor;
    $[14] = onLoadMore;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <PopoverContent align="end" side="bottom" className="flex max-h-[min(280px,50vh)] w-64 flex-col overflow-hidden p-2" onOpenAutoFocus={_temp8} onWheel={_temp9}>{t4}{t6}{t7}</PopoverContent>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t3 || $[20] !== t8) {
    t9 = <Popover>{t3}{t8}</Popover>;
    $[19] = t3;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
function _temp9(event_0) {
  return event_0.stopPropagation();
}
function _temp8(event) {
  return event.preventDefault();
}
function _temp7(s_1) {
  return s_1.listCursor;
}
function _temp6(s_0) {
  return s_0.conversationId;
}
function _temp5(s) {
  return s.conversations;
}
export function ChatbotPanel() {
  const {
    primaryColor
  } = useContext(ColorModeContext);
  const open = useChatbotStore(s => s.open);
  const pending = useChatbotStore(s_0 => s_0.pending);
  const error = useChatbotStore(s_1 => s_1.error);
  const messages = useChatbotStore(s_2 => s_2.messages);
  const lastQuestion = useChatbotStore(s_3 => s_3.lastQuestion);
  const unsaved = useChatbotStore(s_4 => s_4.unsaved);
  const historyLoading = useChatbotStore(s_5 => s_5.historyLoading);
  const setOpen = useChatbotStore(s_6 => s_6.setOpen);
  const newChat = useChatbotStore(s_7 => s_7.newChat);
  const [draft, setDraft] = useState("");
  const [mounted, setMounted] = useState(false);
  const [hydrated, setHydrated] = useState(() => useChatbotStore.persist.hasHydrated());
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const stickToBottomRef = useRef(true);
  const preserveScrollRef = useRef<number | null>(null);
  const params = useParams();
  const branchId = typeof params.branch_id === "string" ? params.branch_id : "";
  useEffect(() => {
    setMounted(true);
    setHydrated(useChatbotStore.persist.hasHydrated());
    return useChatbotStore.persist.onFinishHydration(() => setHydrated(true));
  }, []);
  useEffect(() => {
    if (!open) return;
    const node = listRef.current;
    if (!node) return;
    if (preserveScrollRef.current != null) {
      const previous = preserveScrollRef.current;
      preserveScrollRef.current = null;
      node.scrollTop += node.scrollHeight - previous;
      return;
    }
    if (!stickToBottomRef.current) return;
    node.scrollTop = node.scrollHeight;
  }, [messages, pending, error, open]);
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);
  const dropConversation = useCallback((id: string, clearThread: boolean) => {
    const state = useChatbotStore.getState();
    const conversationByBranch = {
      ...state.conversationByBranch
    };
    for (const [branch, saved] of Object.entries(conversationByBranch)) {
      if (saved === id) delete conversationByBranch[branch];
    }
    useChatbotStore.setState({
      conversations: state.conversations.filter(item => item.id !== id),
      conversationByBranch,
      ...(state.conversationId === id ? {
        conversationId: null,
        ...(clearThread ? {
          messages: [],
          messagesCursor: null,
          unsaved: false
        } : {})
      } : {}),
      historyLoading: false
    });
  }, []);
  const rememberConversation = useCallback((id_0: string | null) => {
    const conversationByBranch_0 = {
      ...useChatbotStore.getState().conversationByBranch
    };
    if (id_0) conversationByBranch_0[branchId] = id_0;else delete conversationByBranch_0[branchId];
    return conversationByBranch_0;
  }, [branchId]);
  useEffect(() => {
    if (!open || !hydrated) return;
    let cancelled = false;
    const state_0 = useChatbotStore.getState();
    if (state_0.branchKey !== branchId) {
      const remembered = state_0.conversationByBranch[branchId] ?? null;
      useChatbotStore.setState({
        branchKey: branchId,
        conversationId: remembered,
        messages: [],
        messagesCursor: null,
        conversations: [],
        listCursor: null,
        unsaved: false,
        error: null,
        historyLoading: Boolean(remembered)
      });
    }
    const load = async () => {
      try {
        const page = await listConversations();
        if (cancelled) return;
        useChatbotStore.setState({
          conversations: page.items,
          listCursor: page.nextCursor
        });
        const id_2 = useChatbotStore.getState().conversationId;
        if (!id_2 || useChatbotStore.getState().messages.length > 0) {
          useChatbotStore.setState({
            historyLoading: false
          });
          return;
        }
        useChatbotStore.setState({
          historyLoading: true
        });
        const transcript = await listMessages(id_2);
        if (cancelled || useChatbotStore.getState().conversationId !== id_2) return;
        if (useChatbotStore.getState().pending) {
          useChatbotStore.setState({
            messagesCursor: transcript.nextCursor,
            historyLoading: false
          });
          return;
        }
        useChatbotStore.setState({
          messages: transcript.items,
          messagesCursor: transcript.nextCursor,
          historyLoading: false
        });
      } catch (err) {
        if (cancelled || err instanceof ChatRequestError && err.status === 401) {
          return;
        }
        const id_1 = useChatbotStore.getState().conversationId;
        if (err instanceof ChatRequestError && err.status === 404 && id_1) {
          dropConversation(id_1, true);
          return;
        }
        useChatbotStore.setState({
          historyLoading: false,
          error: err instanceof ChatRequestError ? err.message : "something went wrong"
        });
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, hydrated, branchId, dropConversation]);
  const openConversation = useCallback(async (id_3: string) => {
    if (id_3 === useChatbotStore.getState().conversationId) return;
    stickToBottomRef.current = true;
    useChatbotStore.setState({
      conversationId: id_3,
      messages: [],
      messagesCursor: null,
      unsaved: false,
      error: null,
      historyLoading: true,
      conversationByBranch: rememberConversation(id_3)
    });
    try {
      const transcript_0 = await listMessages(id_3);
      if (useChatbotStore.getState().conversationId !== id_3) return;
      useChatbotStore.setState({
        messages: transcript_0.items,
        messagesCursor: transcript_0.nextCursor,
        historyLoading: false
      });
    } catch (err_0) {
      if (useChatbotStore.getState().conversationId !== id_3) return;
      if (err_0 instanceof ChatRequestError && err_0.status === 401) return;
      if (err_0 instanceof ChatRequestError && err_0.status === 404) {
        dropConversation(id_3, true);
        useChatbotStore.setState({
          error: err_0.message
        });
        return;
      }
      useChatbotStore.setState({
        historyLoading: false,
        error: err_0 instanceof ChatRequestError ? err_0.message : "something went wrong"
      });
    }
  }, [dropConversation, rememberConversation]);
  const loadOlderMessages = useCallback(async () => {
    const state_1 = useChatbotStore.getState();
    if (!state_1.messagesCursor || !state_1.conversationId || state_1.historyLoading || state_1.pending) {
      return;
    }
    const id_4 = state_1.conversationId;
    const cursor = state_1.messagesCursor;
    preserveScrollRef.current = listRef.current?.scrollHeight ?? 0;
    useChatbotStore.setState({
      historyLoading: true
    });
    try {
      const page_0 = await listMessages(id_4, cursor);
      if (useChatbotStore.getState().conversationId !== id_4) {
        preserveScrollRef.current = null;
        return;
      }
      useChatbotStore.setState({
        messages: prependOlderMessages(useChatbotStore.getState().messages, page_0.items),
        messagesCursor: page_0.nextCursor,
        historyLoading: false
      });
    } catch (err_1) {
      preserveScrollRef.current = null;
      if (err_1 instanceof ChatRequestError && err_1.status === 401) {
        useChatbotStore.setState({
          historyLoading: false
        });
        return;
      }
      if (err_1 instanceof ChatRequestError && err_1.status === 404) {
        dropConversation(id_4, true);
        return;
      }
      useChatbotStore.setState({
        historyLoading: false,
        error: err_1 instanceof ChatRequestError ? err_1.message : "something went wrong"
      });
    }
  }, [dropConversation]);
  const loadMoreConversations = useCallback(async () => {
    const cursor_0 = useChatbotStore.getState().listCursor;
    if (!cursor_0) return;
    try {
      const page_1 = await listConversations(cursor_0);
      const current = useChatbotStore.getState().conversations;
      const seen = new Set(current.map(item_0 => item_0.id));
      useChatbotStore.setState({
        conversations: [...current, ...page_1.items.filter(item_1 => !seen.has(item_1.id))],
        listCursor: page_1.nextCursor
      });
    } catch (err_2) {
      if (err_2 instanceof ChatRequestError && err_2.status === 401) return;
      useChatbotStore.setState({
        error: err_2 instanceof ChatRequestError ? err_2.message : "something went wrong"
      });
    }
  }, []);
  const onMessagesScroll = () => {
    const node_0 = listRef.current;
    if (!node_0) return;
    const distance = node_0.scrollHeight - node_0.scrollTop - node_0.clientHeight;
    stickToBottomRef.current = distance < 80;
    if (node_0.scrollTop < 48) void loadOlderMessages();
  };
  const send = useCallback(async (question: string, isRetry = false) => {
    const trimmed = question.trim();
    const busy = useChatbotStore.getState();
    if (!trimmed || busy.pending || busy.historyLoading) return;
    const state_2 = useChatbotStore.getState();
    const conversationId = state_2.conversationId;
    stickToBottomRef.current = true;
    if (!isRetry) {
      useChatbotStore.setState({
        messages: [...state_2.messages, {
          role: "user",
          content: trimmed
        }]
      });
    }
    useChatbotStore.setState({
      pending: true,
      error: null,
      unsaved: false,
      lastQuestion: trimmed
    });
    const sentOnBranch = branchId;
    const applyAnswer = (answer: string, sources: ChatSource[], savedId: string | undefined) => {
      const current_0 = useChatbotStore.getState();
      if (current_0.branchKey !== sentOnBranch) {
        useChatbotStore.setState({
          pending: false
        });
        return;
      }
      useChatbotStore.setState({
        pending: false,
        unsaved: !savedId,
        conversationId: savedId ?? null,
        conversationByBranch: rememberConversation(savedId ?? null),
        messages: [...current_0.messages, {
          role: "assistant",
          content: answer,
          sources
        }]
      });
      if (!savedId) return;
      void listConversations().then(page_2 => {
        if (useChatbotStore.getState().branchKey !== branchId) return;
        useChatbotStore.setState({
          conversations: page_2.items,
          listCursor: page_2.nextCursor
        });
      }).catch(() => undefined);
    };
    try {
      let result;
      try {
        result = await postChat(trimmed, conversationId);
      } catch (err_4) {
        if (err_4 instanceof ChatRequestError && err_4.status === 404 && conversationId) {
          dropConversation(conversationId, false);
          result = await postChat(trimmed);
        } else {
          throw err_4;
        }
      }
      applyAnswer(result.answer, result.sources, result.conversationId);
    } catch (err_3) {
      if (err_3 instanceof ChatRequestError && err_3.status === 401) return;
      if (useChatbotStore.getState().branchKey !== branchId) {
        useChatbotStore.setState({
          pending: false
        });
        return;
      }
      useChatbotStore.setState({
        pending: false,
        error: err_3 instanceof ChatRequestError ? err_3.message : "something went wrong"
      });
    }
  }, [branchId, dropConversation, rememberConversation]);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const value = draft;
    setDraft("");
    if (inputRef.current) inputRef.current.style.height = "44px";
    void send(value);
  };
  const onComposerKeyDown = (event_0: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event_0.key === "Enter" && !event_0.shiftKey) {
      event_0.preventDefault();
      const value_0 = draft;
      setDraft("");
      if (inputRef.current) inputRef.current.style.height = "44px";
      void send(value_0);
    }
  };
  const resizeComposer = (el: HTMLTextAreaElement) => {
    el.style.height = "44px";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };
  if (!mounted) return null;
  return createPortal(<>
      {open && <div className="overflow-hidden rounded-2xl border border-border bg-background shadow-[0_16px_40px_hsl(var(--overlay)/0.22)]" style={{
      position: "fixed",
      right: 16,
      bottom: 80,
      left: "auto",
      top: "auto",
      zIndex: 9990,
      display: "flex",
      flexDirection: "column",
      width: "min(400px, calc(100vw - 2rem))",
      height: "min(640px, calc(100vh - 6.5rem))",
      maxHeight: "calc(100vh - 6.5rem)"
    }}>
          <div className="flex shrink-0 items-center justify-between gap-2 px-3 py-2.5 text-white" style={{
        backgroundColor: primaryColor
      }}>
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white/15">
                <Bot className="size-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold leading-tight">Assistant</p>
                <p className="text-[11px] leading-tight text-white/75">
                  {pending ? "Thinking…" : "Club policies & docs"}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center">
              <SessionPicker disabled={pending} onSelect={id_5 => void openConversation(id_5)} onLoadMore={() => void loadMoreConversations()} />
              <Button type="button" variant="text" iconOnly className="h-8 text-white hover:bg-white/10" icon={<RotateCcw className="size-4" />} buttonLabel="New chat" onClick={newChat} disabled={pending || messages.length === 0} />
              <Button type="button" variant="text" iconOnly className="h-8 text-white hover:bg-white/10" icon={<X className="size-4" />} buttonLabel="Close assistant" onClick={() => setOpen(false)} />
            </div>
          </div>

          <div ref={listRef} onScroll={onMessagesScroll} className="styled-scrollbar min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-3 py-3" style={{
        flex: "1 1 0%",
        minHeight: 0
      }}>
            {messages.length === 0 && !pending && !historyLoading && <div className="flex h-full flex-col items-center justify-center gap-4 px-2 text-center">
                <span className="flex size-12 items-center justify-center rounded-full" style={{
            backgroundColor: `${primaryColor}1a`,
            color: primaryColor
          }}>
                  <Bot className="size-6" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Ask the club assistant
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Answers come from tenant documents. Try one of these:
                  </p>
                </div>
                <ul className="flex w-full flex-col gap-2">
                  {STARTER_PROMPTS.map(prompt => <li key={prompt}>
                      <button type="button" onClick={() => void send(prompt)} className="w-full rounded-xl border border-border bg-muted/60 px-3 py-2 text-left text-xs text-foreground transition-colors hover:border-primary hover:bg-muted">
                        {prompt}
                      </button>
                    </li>)}
                </ul>
              </div>}

            <div className="flex flex-col gap-3">
              {historyLoading && messages.length === 0 && <p className="text-center text-sm text-muted-foreground">
                  Loading chat…
                </p>}
              {messages.map((message, index) => <div key={message.id ?? `${message.role}-${index}`} className={cn("max-w-[92%] break-words rounded-2xl px-3 py-2 text-sm leading-relaxed", message.role === "user" ? "ml-auto rounded-br-md text-white" : "mr-auto rounded-bl-md border border-border bg-muted/70 text-foreground")} style={message.role === "user" ? {
            backgroundColor: primaryColor
          } : undefined}>
                  {message.role === "assistant" ? <div className="chatbot-md space-y-2 overflow-x-auto [&_a]:underline [&_code]:rounded [&_code]:bg-background/80 [&_code]:px-1 [&_li]:break-words [&_ol]:list-decimal [&_ol]:pl-4 [&_p]:m-0 [&_p]:break-words [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-background/80 [&_pre]:p-2 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-4" dangerouslySetInnerHTML={{
              __html: renderAnswer(message.content, message.sources)
            }} /> : <p className="whitespace-pre-wrap break-words">
                      {message.content}
                    </p>}
                  {message.role === "assistant" && message.sources && message.sources.length > 0 && <SourceButton sources={message.sources} />}
                </div>)}

              {pending && <TypingIndicator color={primaryColor} />}

              {unsaved && !pending && <p className="text-xs text-muted-foreground">
                  This reply wasn&apos;t saved, so the next question starts a
                  new chat.
                </p>}

              {error && <div className="space-y-2 rounded-xl border border-danger/30 bg-danger-50 px-3 py-2 text-sm text-danger">
                  <p>{error}</p>
                  {lastQuestion && <Button type="button" variant="outline" typeColor="danger" size="small" onClick={() => void send(lastQuestion, true)} disabled={pending}>
                      Resend
                    </Button>}
                </div>}
            </div>
          </div>

          <form onSubmit={onSubmit} className="shrink-0 border-t border-border bg-background p-2.5">
            <div className="flex items-end gap-2 rounded-xl border border-input bg-muted/40 px-2 py-1.5 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
              <textarea ref={inputRef} value={draft} onChange={event_1 => {
            setDraft(event_1.target.value);
            resizeComposer(event_1.target);
          }} onKeyDown={onComposerKeyDown} placeholder="Ask a question…" disabled={pending || historyLoading} rows={1} maxLength={8000} className="max-h-[120px] min-h-[40px] flex-1 resize-none bg-transparent px-1 py-2 text-sm leading-5 placeholder:text-muted-foreground focus-visible:outline-none disabled:opacity-50" />
              <Button type="submit" variant="filled" typeColor="primary" iconOnly className="mb-0.5 size-9 shrink-0 rounded-lg" icon={<SendHorizontal className="size-4" />} buttonLabel="Send" disabled={pending || historyLoading || !draft.trim()} loading={pending} />
            </div>
            <p className="mt-1.5 px-1 text-[10px] text-muted-foreground">
              Enter to send · Shift+Enter for a new line
            </p>
          </form>
        </div>}

      <button type="button" aria-label={open ? "Close assistant" : "Open assistant"} onClick={() => setOpen(!open)} className="flex size-14 items-center justify-center rounded-full text-white shadow-[0_8px_24px_hsl(var(--overlay)/0.18)] transition hover:scale-105 active:scale-95" style={{
      position: "fixed",
      right: 16,
      bottom: 16,
      left: "auto",
      top: "auto",
      zIndex: 9990,
      backgroundColor: primaryColor
    }}>
        {open ? <X className="size-6" /> : <Bot className="size-6" />}
      </button>
    </>, document.body);
}